import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AppShell as ConsoleShell,
  BrandMark,
  Text,
  ThemeToggle,
  NavRail,
  BottomNav,
  CommandMenu,
  Icon,
  Icons,
  IconButton,
  Menu,
  MenuItem,
  useColorMode,
  type CommandItem,
  type LucideIcon,
  type NavModelItem,
} from '../ui';
import { useAuth } from '../lib/auth';
import { useOperationsStream } from '../lib/useOperationsStream';
import { ChatPanel, OPEN_CHAT_EVENT } from './ChatPanel';
import { NotificationsBell } from './NotificationsBell';
import { VersionFooter } from './VersionFooter';
import { NAV_SECTIONS, workspaceLabel } from './appShellNav';

/** Section icons (lucide via the ui Icon layer). Ordered to match {@link NAV_SECTIONS}. */
const SECTION_ICON: Record<string, LucideIcon> = {
  dashboard: Icons.LayoutDashboard,
  agents: Icons.Bot,
  runs: Icons.Activity,
  approvals: Icons.ShieldCheck,
  connections: Icons.Plug,
  admin: Icons.Settings,
};

const NAV_ITEMS: NavModelItem[] = NAV_SECTIONS.map((s) => ({
  key: s.key,
  label: s.label,
  to: s.to,
  icon: SECTION_ICON[s.key]!,
}));

/**
 * The authenticated application shell — the Chakra "Operator's Console" frame wired to
 * the app's data. It re-presents every behaviour of the previous MUI AppLayout using the
 * design-system {@link ConsoleShell}: the NavRail (desktop) / BottomNav (mobile), a slim
 * top bar with the command menu (⌘K), theme toggle, notifications bell and an account
 * menu (workspace label + sign-out), the version footer, and the quick-chat launcher.
 *
 * Pages still render MUI content inside this Chakra frame (staged migration).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { workspaceId, workspaces, signOut } = useAuth();
  const navigate = useNavigate();
  const { toggleColorMode } = useColorMode();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Live notification badge: subscribe to the operations SSE stream (invalidates the
  // notifications queries the bell reads).
  useOperationsStream(workspaceId);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeSection = NAV_SECTIONS.find((s) => pathname.startsWith(s.to));
  const label = workspaceLabel(workspaces);

  const commands = useMemo<CommandItem[]>(() => {
    const nav: CommandItem[] = NAV_SECTIONS.map((s) => ({
      id: `nav-${s.key}`,
      label: s.label,
      group: 'Navigate',
      keywords: `go to open ${s.label}`,
      icon: SECTION_ICON[s.key]!,
      perform: () => void navigate({ to: s.to }),
    }));
    const actions: CommandItem[] = [
      {
        id: 'action-new-agent',
        label: 'New agent',
        group: 'Actions',
        keywords: 'create build agent',
        icon: Icons.Plus,
        perform: () => void navigate({ to: '/app/agents' }),
      },
      {
        id: 'action-new-chat',
        label: 'New chat',
        group: 'Actions',
        keywords: 'assistant quick chat ask',
        icon: Icons.MessageCircle,
        perform: () => window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT)),
      },
      {
        id: 'action-toggle-theme',
        label: 'Toggle theme',
        group: 'Actions',
        keywords: 'dark light mode appearance',
        icon: Icons.SunMoon,
        perform: () => toggleColorMode(),
      },
      {
        id: 'action-sign-out',
        label: 'Sign out',
        group: 'Actions',
        keywords: 'logout log out exit',
        icon: Icons.LogOut,
        perform: () => void signOut(),
      },
    ];
    return [...nav, ...actions];
  }, [navigate, toggleColorMode, signOut]);

  const rail = (
    <NavRail
      items={NAV_ITEMS}
      currentPath={pathname}
      linkComponent={Link}
      header={<BrandMark />}
    />
  );

  const bottomNav = (
    <BottomNav items={NAV_ITEMS} currentPath={pathname} linkComponent={Link} />
  );

  const topBarEnd = (
    <>
      <IconButton
        aria-label="Open command menu"
        data-testid="command-menu-trigger"
        variant="ghost"
        onClick={() => setCmdOpen(true)}
        icon={<Icon as={Icons.Search} size="md" tone="current" />}
      />
      <ThemeToggle />
      <NotificationsBell workspaceId={workspaceId} />
      <Menu
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        label="Account"
        width="14rem"
        data-testid="account-menu"
        trigger={
          <IconButton
            aria-label="Account menu"
            data-testid="account-menu-trigger"
            variant="ghost"
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            onClick={() => setAccountOpen((o) => !o)}
            icon={<Icon as={Icons.CircleUserRound} size="md" tone="current" />}
          />
        }
      >
        {label && (
          <MenuItem disabled data-testid="account-workspace">
            <Text tone="muted" size="xs">
              Workspace
            </Text>
            <Text weight="medium" data-testid="workspace-label">
              {label}
            </Text>
          </MenuItem>
        )}
        <MenuItem
          data-testid="sign-out"
          onClick={() => {
            setAccountOpen(false);
            void signOut();
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <ConsoleShell
      rail={rail}
      bottomNav={bottomNav}
      brand={<BrandMark compact />}
      topBarStart={
        activeSection && (
          <Text weight="semibold" truncate>
            {activeSection.label}
          </Text>
        )
      }
      topBarEnd={topBarEnd}
      footer={<VersionFooter />}
      overlays={
        <>
          <ChatPanel />
          <CommandMenu open={cmdOpen} onOpenChange={setCmdOpen} commands={commands} />
        </>
      }
    >
      {children}
    </ConsoleShell>
  );
}
